import * as API from '../api.js';

const CONFETTI_SESSION_KEY = 'mosaic_first_confetti_shown';
const CONFETTI_DURATION_MS = 1800;
const CONFETTI_FADE_OUT_START_RATIO = 0.72;
const CONFETTI_GRAVITY_PX_S2 = 460;
const CONFETTI_MIN_PIECES = 90;
const CONFETTI_MAX_PIECES = 170;
const CONFETTI_COLORS = [
    '#2b6cb0',
    '#3182ce',
    '#38a169',
    '#d69e2e',
    '#dd6b20',
    '#e53e3e',
];

let _activeRun = null;

function _prefersReducedMotion() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
        return Boolean(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (_e) {
        return false;
    }
}

function _sessionFlagSet() {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    try {
        return window.sessionStorage.getItem(CONFETTI_SESSION_KEY) === '1';
    } catch (_e) {
        return false;
    }
}

function _setSessionFlag() {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
        window.sessionStorage.setItem(CONFETTI_SESSION_KEY, '1');
    } catch (_e) {}
}

function _rand(min, max) {
    return min + (Math.random() * (max - min));
}

function _pieceCount(widthPx) {
    const widthBased = Math.round(Math.max(320, Number(widthPx || 0)) / 10);
    return Math.max(CONFETTI_MIN_PIECES, Math.min(CONFETTI_MAX_PIECES, widthBased));
}

function _createPieces(count, width, height) {
    const safeCount = Math.max(1, Number(count || 0));
    const pieces = [];
    for (let i = 0; i < safeCount; i += 1) {
        pieces.push({
            x: _rand(-40, width + 40),
            y: _rand(-height * 0.55, -12),
            vx: _rand(-90, 90),
            vy: _rand(120, 320),
            w: _rand(5, 11),
            h: _rand(7, 15),
            rotation: _rand(0, Math.PI * 2),
            spin: _rand(-7.5, 7.5),
            color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
            alpha: _rand(0.8, 1),
        });
    }
    return pieces;
}

function _teardown(reason = 'done') {
    const run = _activeRun;
    if (!run) return;
    _activeRun = null;
    if (run.rafId && typeof window !== 'undefined') {
        window.cancelAnimationFrame(run.rafId);
    }
    if (typeof window !== 'undefined' && run.resizeHandler) {
        window.removeEventListener('resize', run.resizeHandler);
    }
    if (run.canvas && run.canvas.parentNode) {
        run.canvas.parentNode.removeChild(run.canvas);
    }
    API.logUiFlow('confetti', 'teardown', reason, {}).catch(() => {});
}

function _launch(jobId = '', options = {}) {
    if (typeof document === 'undefined' || typeof window === 'undefined') return false;
    if (_activeRun) return false;
    const allowRepeated = Boolean(options && options.allowRepeated);
    const logAction = String((options && options.logAction) || 'first_generation');
    if (_prefersReducedMotion()) {
        API.logUiFlow('confetti', logAction, 'skipped_reduced_motion', {
            job_id: String(jobId || ''),
        }).catch(() => {});
        return false;
    }
    if (!allowRepeated && _sessionFlagSet()) {
        API.logUiFlow('confetti', logAction, 'skipped_session_seen', {
            job_id: String(jobId || ''),
        }).catch(() => {});
        return false;
    }

    const canvas = document.createElement('canvas');
    canvas.className = 'confetti-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;

    const dpr = Math.min(2, Number(window.devicePixelRatio || 1));
    const run = {
        canvas,
        ctx,
        dpr,
        width: 0,
        height: 0,
        pieces: [],
        rafId: 0,
        resizeHandler: null,
    };

    const resize = () => {
        run.width = Math.max(1, Math.floor(window.innerWidth || document.documentElement?.clientWidth || 1));
        run.height = Math.max(1, Math.floor(window.innerHeight || document.documentElement?.clientHeight || 1));
        canvas.style.width = `${run.width}px`;
        canvas.style.height = `${run.height}px`;
        canvas.width = Math.floor(run.width * dpr);
        canvas.height = Math.floor(run.height * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    run.resizeHandler = resize;

    resize();
    run.pieces = _createPieces(_pieceCount(run.width), run.width, run.height);
    document.body.appendChild(canvas);
    window.addEventListener('resize', resize, { passive: true });
    if (!allowRepeated) {
        _setSessionFlag();
    }
    _activeRun = run;

    const startedAt = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    let lastTs = startedAt;
    const fadeStartMs = Math.max(200, Math.round(CONFETTI_DURATION_MS * CONFETTI_FADE_OUT_START_RATIO));

    const frame = (ts) => {
        if (_activeRun !== run) return;
        const now = Number(ts || 0);
        const elapsedMs = Math.max(0, now - startedAt);
        const dtMs = Math.min(50, Math.max(8, now - lastTs));
        lastTs = now;
        const dt = dtMs / 1000;

        let fade = 1;
        if (elapsedMs > fadeStartMs) {
            const remain = Math.max(0, CONFETTI_DURATION_MS - elapsedMs);
            fade = Math.max(0, remain / Math.max(1, CONFETTI_DURATION_MS - fadeStartMs));
        }

        ctx.clearRect(0, 0, run.width, run.height);
        for (let i = 0; i < run.pieces.length; i += 1) {
            const p = run.pieces[i];
            p.vy += CONFETTI_GRAVITY_PX_S2 * dt;
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            p.rotation += p.spin * dt;

            if (p.y > run.height + 24) {
                p.y = _rand(-run.height * 0.45, -8);
                p.x = _rand(-30, run.width + 30);
                p.vy = _rand(120, 300);
                p.vx = _rand(-90, 90);
            }

            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rotation);
            ctx.globalAlpha = Math.max(0, Math.min(1, p.alpha * fade));
            ctx.fillStyle = p.color;
            ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
            ctx.restore();
        }
        ctx.globalAlpha = 1;

        if (elapsedMs >= CONFETTI_DURATION_MS) {
            _teardown('completed');
            return;
        }
        run.rafId = window.requestAnimationFrame(frame);
    };

    run.rafId = window.requestAnimationFrame(frame);
    API.logUiFlow('confetti', logAction, 'shown', {
        job_id: String(jobId || ''),
        duration_ms: CONFETTI_DURATION_MS,
        piece_count: run.pieces.length,
    }).catch(() => {});
    return true;
}

export function launchFirstMosaicConfettiIfEligible(options = {}) {
    const lifetimeTotal = Number(options && options.lifetimeTotal);
    if (!Number.isFinite(lifetimeTotal) || lifetimeTotal !== 1) return false;
    const jobId = options && options.jobId ? String(options.jobId) : '';
    return _launch(jobId, { allowRepeated: false, logAction: 'first_generation' });
}

export function launchMosaicConfetti(options = {}) {
    const jobId = options && options.jobId ? String(options.jobId) : '';
    return _launch(jobId, { allowRepeated: true, logAction: 'mosaic_complete' });
}

export function clearConfetti() {
    _teardown('forced_clear');
}
